// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { Text, mergeStyles } from '@fluentui/react';
import { ChatMessage as FluentChatMessage } from '@fluentui-contrib/react-chat';
import { _formatString } from '@internal/acs-ui-common';
import React, { useCallback, useMemo } from 'react';
import { chatMessageDateStyle, chatMessageAuthorStyle, chatMessageDateFailedStyle } from '../../styles/ChatMessageComponent.styles';
import { useIdentifiers } from '../../../identifiers/IdentifierProvider';
import { useTheme } from '../../../theming';
import { InlineImageOptions } from '../ChatMessageContent';
import { ChatMessage } from '../../../types/ChatMessage';
import { MessageThreadStrings } from '../../MessageThread';
import { AttachmentMenuAction, ComponentSlotStyle } from '../../../types';
import { AttachmentMetadata } from '@internal/acs-ui-common';
import { _AttachmentDownloadCards } from '../../Attachment/AttachmentDownloadCards';
import { useLocale } from '../../../localization';
import { createStyleFromV8Style } from '../../styles/v8StyleShim';
import { mergeClasses } from '@fluentui/react-components';
import { useChatMessageStyles, useChatMessageCommonStyles } from '../../styles/MessageThread.styles';
import { generateCustomizedTimestamp, generateDefaultTimestamp, getMessageBubbleContent, getMessageEditedDetails } from '../../utils/ChatMessageComponentUtils';
type ChatMessageComponentAsMessageBubbleProps = {
  message: ChatMessage;
  messageContainerStyle?: ComponentSlotStyle;
  showDate?: boolean;
  strings: MessageThreadStrings;
  userId: string;
  /**
   * Whether to overlap avatar and message when the view is width constrained.
   */
  shouldOverlapAvatarAndMessage: boolean;
  /**
   * Optional callback to render message attachments in the message component.
   */
  onRenderAttachmentDownloads?: (message: ChatMessage) => JSX.Element;
  /**
   * Optional callback to define custom actions for attachments.
   */
  actionsForAttachment?: (attachment: AttachmentMetadata, message?: ChatMessage) => AttachmentMenuAction[];
  /**
   * Optional function to provide customized date format.
   * @beta
   */
  onDisplayDateTimeString?: (messageDate: Date) => string;
  /**
   * Optional callback called when an inline image is clicked.
   * @beta
   */
  inlineImageOptions?: InlineImageOptions;
};

/** @private */
const MessageBubble = (props: ChatMessageComponentAsMessageBubbleProps): JSX.Element => {
  const ids = useIdentifiers();
  const theme = useTheme();
  const locale = useLocale();
  const {
    userId,
    message,
    showDate,
    messageContainerStyle,
    strings,
    onRenderAttachmentDownloads,
    inlineImageOptions,
    shouldOverlapAvatarAndMessage,
    actionsForAttachment,
    onDisplayDateTimeString
  } = props;
  const formattedTimestamp = useMemo(() => {
    const defaultTimeStamp = message.createdOn ? generateDefaultTimestamp(message.createdOn, showDate, strings) : undefined;
    const customTimestamp = message.createdOn ? generateCustomizedTimestamp(message.createdOn, locale, onDisplayDateTimeString) : '';
    return customTimestamp || defaultTimeStamp;
  }, [locale, message.createdOn, onDisplayDateTimeString, showDate, strings]);
  const getMessageDetails = useCallback(() => {
    return getMessageEditedDetails(message, theme, strings.editedTag);
  }, [strings.editedTag, theme, message]);
  const getContent = useCallback(() => {
    return getMessageBubbleContent(message, strings, userId, inlineImageOptions, onRenderAttachmentDownloads, actionsForAttachment);
  }, [actionsForAttachment, inlineImageOptions, message, onRenderAttachmentDownloads, strings, userId]);
  const isBlockedMessage =
  // eslint-disable-next-line no-constant-binary-expression
  false;
  const chatMessageCommonStyles = useChatMessageCommonStyles();
  const chatMessageStyles = useChatMessageStyles();
  const chatItemMessageContainerClassName = mergeClasses(chatMessageCommonStyles.body, chatMessageStyles.body,
  // disable placeholder functionality for GA releases as it might confuse users
  chatMessageCommonStyles.bodyWithPlaceholderImage, isBlockedMessage ? chatMessageCommonStyles.blocked : props.message.status === 'failed' ? chatMessageCommonStyles.failed : undefined, shouldOverlapAvatarAndMessage ? chatMessageStyles.avatarOverlap : chatMessageStyles.avatarNoOverlap, message.attached === 'top' || message.attached === false ? chatMessageStyles.bodyWithAvatar : chatMessageStyles.bodyWithoutAvatar,
  // messageContainerStyle used in className and style prop as style prop can't handle CSS selectors
  mergeStyles(messageContainerStyle));
  const attached = message.attached === true ? 'center' : message.attached === 'bottom' ? 'bottom' : 'top';
  const chatMessage = <>
      <div key={props.message.messageId}>
        <FluentChatMessage attached={attached} key={props.message.messageId} root={{
        className: chatMessageStyles.root
      }} author={<Text className={chatMessageAuthorStyle}>{message.senderDisplayName}</Text>} body={{
        className: chatItemMessageContainerClassName,
        style: {
          ...createStyleFromV8Style(messageContainerStyle)
        }
      }} data-testid="chat-composite-message" timestamp={<Text className={props.message.status === 'failed' ? chatMessageDateFailedStyle(theme) : chatMessageDateStyle(theme)} data-testid={ids.messageTimestamp}>
              {formattedTimestamp}
            </Text>} details={getMessageDetails()}>
          {getContent()}
        </FluentChatMessage>
      </div>
    </>;
  return chatMessage;
};

/** @private */
export const ChatMessageComponentAsMessageBubble = React.memo(MessageBubble);